import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CheckCircle, Home, UserCircle } from 'lucide-react';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId, memberType, category, amount, paymentId, purpose } = location.state || {};

  // Define content based on payment category
  const getSuccessContent = () => {
    // Check if category contains specific keywords
    const categoryLower = (category || '').toLowerCase();
    
    if (categoryLower.includes('research') || categoryLower.includes('paper')) {
      return {
        title: 'Research Paper Purchase Successful!',
        subtitle: 'Your research paper access is now available',
        welcomeHeading: 'Thank You for Your Purchase!',
        welcomeText: 'Your research paper purchase has been processed successfully.',
        nextSteps: [
          'You will receive a confirmation email with download links',
          'Access your purchased paper(s) from your account dashboard',
          'Papers are available in PDF format for immediate download',
          'For citation help or questions, contact our research team'
        ]
      };
    } else if (categoryLower.includes('event')) {
      return {
        title: 'Event Registration Successful!',
        subtitle: 'You are now registered for the event',
        welcomeHeading: 'Registration Confirmed!',
        welcomeText: 'Your event registration has been processed successfully.',
        nextSteps: [
          'You will receive a confirmation email with event details',
          'Event joining link/venue details will be shared 24 hours before',
          'Add the event to your calendar to get reminders',
          'Contact us if you have any special requirements or questions'
        ]
      };
    } else if (categoryLower.includes('donation')) {
      return {
        title: 'Thank You for Your Donation!',
        subtitle: 'Your contribution makes a difference',
        welcomeHeading: 'Donation Received!',
        welcomeText: 'Your generous contribution has been processed successfully.',
        nextSteps: [
          'You will receive a tax receipt via email within 24 hours',
          'Your donation supports our socio-economic development initiatives',
          'Track the impact of your contribution on our website',
          'Consider becoming a regular donor to maximize your impact'
        ]
      };
    } else {
      // Default to membership content
      return {
        title: 'Payment Successful!',
        subtitle: 'Your membership registration is now complete',
        welcomeHeading: 'Welcome to CSED!',
        welcomeText: 'Your membership application has been processed successfully.',
        nextSteps: [
          'You will receive a confirmation email with your membership details',
          'Your membership number will be sent within 24 hours',
          'You can now access all member-exclusive content and resources',
          'Our team will contact you for onboarding and orientation'
        ]
      };
    }
  };

  const content = getSuccessContent();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-16 pt-32">
        <div className="container mx-auto px-4 text-center">
          <CheckCircle className="w-20 h-20 text-white mx-auto mb-4 animate-bounce" />
          <h1 className="text-4xl font-bold mb-4">{content.title}</h1>
          <p className="text-green-100 max-w-2xl mx-auto text-lg">
            {content.subtitle}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <Card className="max-w-2xl mx-auto border-2 border-green-200">
          <CardContent className="pt-8 text-center">
            <div className="mb-6">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-16 h-16 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#1E3A5F] mb-2">
                {content.welcomeHeading}
              </h2>
              <p className="text-gray-600">
                {content.welcomeText}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 text-left">
              <h3 className="font-semibold text-[#1E3A5F] mb-3">What happens next?</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                {content.nextSteps.map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>

            {(applicationId || paymentId) && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-2">
                {paymentId && (
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">Payment ID:</span>{' '}
                    <span className="font-mono text-xs">{paymentId}</span>
                  </p>
                )}
                {amount && (
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">Amount Paid:</span>{' '}
                    <span className="font-semibold text-green-600">₹{amount.toLocaleString('en-IN')}</span>
                  </p>
                )}
                {applicationId && (
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">Application Reference:</span>{' '}
                    <span className="font-mono">{applicationId.substring(0, 8).toUpperCase()}</span>
                  </p>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-[#1E3A5F] hover:bg-[#1E3A5F]/90"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-[#E87722] text-[#E87722] hover:bg-[#E87722] hover:text-white"
              >
                <UserCircle className="w-4 h-4 mr-2" />
                View Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="max-w-2xl mx-auto mt-8 text-center">
          <p className="text-sm text-gray-600">
            Need help? Contact us at{' '}
            <a href="mailto:info@thecsed.org" className="text-[#E87722] hover:underline">
              info@thecsed.org
            </a>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
